import { CrlAssignee, CrlAvatarColorKey, CrlItemStatus, CrlRequestItem } from '../../models';
import { pickAvatarColorKey } from './avatar-color.util';
import { getCxpContactInitials } from './contact-initials.util';

/** How many assignee avatars the stack shows before collapsing the rest into a `+N` chip. */
export const CRL_ITEM_ADDON_AVATAR_LIMIT = 3;

/** One initial-avatar in the assignee stack. */
export interface CrlItemAddonAvatar {
  /**
   * Unique key for `@for` tracking. Position-qualified rather than id-derived:
   * a contact assignee can arrive with neither a contact id nor a secure-link
   * id, and two such assignees would otherwise collide on one key.
   */
  key: string;
  initials: string;
  colorKey: CrlAvatarColorKey;
  name: string;
}

/**
 * A single entry in a row's add-on slot (Figma "Add-On Slot", node `11158:78373`).
 * Discriminated by `kind`; the row template switches on it, so a new add-on is
 * a new member here plus a builder in {@link CRL_ITEM_ADDON_BUILDERS}.
 */
export type CrlItemAddon =
  | { kind: 'comments'; hasUnread: boolean }
  | { kind: 'notApplicable'; canUndo: boolean }
  | { kind: 'files'; count: number; label: string; hasUnviewed: boolean }
  | { kind: 'textResponse' }
  | { kind: 'assignees'; avatars: CrlItemAddonAvatar[]; overflowCount: number; names: string };

/** Resolves one add-on from an item, or `null` when the item does not qualify for it. */
type CrlItemAddonBuilder = (item: CrlRequestItem) => CrlItemAddon | null;

/**
 * The comments add-on (speech bubble + teal dot when unread).
 *
 * There is no comment COUNT: the live query reports at most one row per item, so
 * existence and unviewed-ness are all the row can know.
 */
const buildCommentsAddon: CrlItemAddonBuilder = (item) =>
  item.hasComments ? { kind: 'comments', hasUnread: !!item.hasUnviewedComments } : null;

/**
 * "Marked as N/A" text, with an Undo link while the item is still the client's
 * to change. Supersedes the files and text-response add-ons.
 *
 * The label stays once the firm accepts the item — it is still N/A — but the
 * Undo goes, matching the overlay's `canUndoNotApplicable`.
 */
const buildNotApplicableAddon: CrlItemAddonBuilder = (item) =>
  isNotApplicable(item) ? { kind: 'notApplicable', canUndo: !item.isAccepted } : null;

/**
 * The files add-on (paperclip + `{n} files`, teal dot when unviewed).
 *
 * `hasUnviewed` is always false: the `/page` read carries no per-item viewed
 * flag.
 */
const buildFilesAddon: CrlItemAddonBuilder = (item) => {
  if (isNotApplicable(item) || item.fileCount <= 0) return null;
  const count = item.fileCount;
  return { kind: 'files', count, label: `${count} ${count === 1 ? 'file' : 'files'}`, hasUnviewed: false };
};

/**
 * The text-response add-on (`View response` button).
 *
 * It is conditional on an answer existing, and the `/page` read carries no
 * `hasAnswer` field — `CrlRequestItem.answer` stays `undefined` until the
 * overlay lazily loads `/item/detail` — so no item qualifies and the add-on
 * never renders. Its slot here fixes its position for when that field exists.
 */
const buildTextResponseAddon: CrlItemAddonBuilder = () => null;

/** The assignee avatar stack — always rightmost, absent when nobody is assigned. */
const buildAssigneesAddon: CrlItemAddonBuilder = (item) => {
  const assignees = item.assignees ?? [];
  if (assignees.length === 0) return null;

  return {
    kind: 'assignees',
    avatars: assignees.slice(0, CRL_ITEM_ADDON_AVATAR_LIMIT).map(toAddonAvatar),
    overflowCount: Math.max(0, assignees.length - CRL_ITEM_ADDON_AVATAR_LIMIT),
    names: assignees.map(assigneeDisplayName).join(', '),
  };
};

/**
 * The add-on slot's documented left-to-right order (Figma annotation
 * `11158:79185`): comments → marked as N/A → files / text response →
 * assignee(s). Each builder decides its own add-on from the item alone, and
 * {@link buildCrlItemAddons} walks this list in order — so an add-on's position
 * is owned by its slot here, never by the order of template blocks.
 */
const CRL_ITEM_ADDON_BUILDERS: readonly CrlItemAddonBuilder[] = [
  buildCommentsAddon,
  buildNotApplicableAddon,
  buildFilesAddon,
  buildTextResponseAddon,
  buildAssigneesAddon,
];

/** The add-ons a row renders, already in spec order. */
export function buildCrlItemAddons(item: CrlRequestItem): CrlItemAddon[] {
  return CRL_ITEM_ADDON_BUILDERS.map((build) => build(item)).filter((addon): addon is CrlItemAddon => addon !== null);
}

function isNotApplicable(item: CrlRequestItem): boolean {
  return item.status === CrlItemStatus.NOT_APPLICABLE;
}

function assigneeDisplayName(assignee: CrlAssignee): string {
  return assignee.name?.trim() || assignee.email?.trim() || 'Unknown';
}

function toAddonAvatar(assignee: CrlAssignee, index: number): CrlItemAddonAvatar {
  // Contacts seed off their contact id, secure-link guests off the link id —
  // the same seed the overlay rail uses, so one person keeps one colour.
  const seed = `#${assignee.contactId ?? assignee.secureLinkId}`;
  return {
    key: `${index}${seed}`,
    initials: getCxpContactInitials(assignee.name, assignee.email),
    colorKey: pickAvatarColorKey(seed),
    name: assigneeDisplayName(assignee),
  };
}
